import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, EyeOff } from "lucide-react";
import { loadPublicSite } from "@/web/publicApi";
import { mediaUrl, BACKEND_URL } from "@/web/useWebApi";
import { themeStyle } from "@/components/web/public/theme";
import { CartProvider } from "@/components/web/public/CartContext";
import { CartDrawer } from "@/components/web/public/CartDrawer";
import { SiteHeader, SiteFooter } from "@/components/web/public/SiteShell";
import { SectionsRenderer } from "@/components/web/public/SectionsRenderer";

// Phase 82 — page publique d'un mini-site (`/s/:slug`, sous-domaine PAG ou domaine perso) + aperçu propriétaire/admin (`?preview=1[&sid=]`)
export default function PublicWebSite({ slug: slugProp = null, base: baseProp = null }) {
  const { slug: slugParam, page: pageParam } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const slug = slugProp || slugParam;
  const base = baseProp ?? `/s/${slug}`;
  const preview = params.get("preview") === "1";
  const sid = params.get("sid");
  const search = preview ? `?preview=1${sid ? `&sid=${sid}` : ""}` : "";
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPayload(null); setError(null);
    loadPublicSite(slug, { preview, sid }).then((r) => setPayload(r.data)).catch((e) => setError(e.response?.status === 401 ? "auth" : "missing"));
  }, [slug, preview, sid]);

  const pages = payload?.site?.pages || [];
  const pageSlug = pageParam || "";
  const page = pages.find((p) => p.slug === pageSlug) || null;

  useEffect(() => {
    if (payload && pageSlug && !page) navigate(`${base || "/"}${search}`, { replace: true });
  }, [payload, pageSlug, page, base, search, navigate]);

  useEffect(() => {
    if (!payload) return;
    const { site } = payload;
    const brand = site.seo?.title || site.brand?.name || "Site";
    document.title = page && page.slug ? `${page.title} — ${brand}` : brand;
    setMeta("description", site.seo?.description || "");
    setMeta("og:title", brand, "property");
    setMeta("og:description", site.seo?.description || "", "property");
    if (site.seo?.og_image_url) setMeta("og:image", mediaUrl(site.seo.og_image_url), "property");
    if (site.brand?.favicon_url) { let l = document.querySelector("link[rel='icon']"); if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); } l.href = mediaUrl(site.brand.favicon_url); }
    if (location.hash) setTimeout(() => { const el = document.getElementById(location.hash.slice(1)); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 150);
    return () => { document.title = "Espace Entreprise PAG — Plateforme RH"; };
  }, [payload, page, location.hash]);

  if (error) return <SiteUnavailable kind={error} />;
  if (!payload) return <div className="min-h-screen flex items-center justify-center text-sm text-[#868E96] gap-2" data-testid="ws-loading"><Loader2 className="h-4 w-4 animate-spin" /> Chargement du site…</div>;

  const { site, owner } = payload;
  const sections = page ? page.sections : site.sections || [];
  const nav = { pages, base, pageSlug, search, sections };
  return (
    <CartProvider slug={slug}>
      <div className="wsite min-h-screen flex flex-col" style={themeStyle(site.theme)} data-testid="ws-root" data-slug={slug} data-page={pageSlug || "home"} translate="no" data-no-translate="1">
        {preview && <div className="bg-[#FFD700] text-[#0A0A0A] text-center text-xs font-bold uppercase tracking-wider py-2 flex items-center justify-center gap-2" data-testid="ws-preview-banner"><EyeOff className="h-3.5 w-3.5" /> Aperçu — {site.published ? "site publié" : "site non publié (visible uniquement par vous)"}</div>}
        <SiteHeader site={site} sections={sections} shopEnabled={site.shop?.enabled} nav={nav} owner={owner} />
        <main className="flex-1"><SectionsRenderer payload={payload} slug={slug} sections={sections} nav={nav} /></main>
        <SiteFooter site={site} owner={owner} nav={nav} />
        {site.shop?.enabled && <CartDrawer slug={slug} base={base} shop={site.shop} preview={preview} theme={site.theme} />}
      </div>
    </CartProvider>
  );
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}='${name}']`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export function SiteUnavailable({ kind = "missing" }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-10 text-center" data-testid="ws-unavailable" translate="no" data-no-translate="1">
      <div className="max-w-md">
        <div className="text-[10px] uppercase tracking-[0.35em] font-bold text-[#FFD700] mb-4">PAG Business Manager</div>
        <h1 className="text-4xl font-black tracking-tight">{kind === "auth" ? "Connexion requise." : "Ce site n'est pas disponible."}</h1>
        <p className="mt-4 text-sm text-white/60">{kind === "auth" ? "L'aperçu est réservé au propriétaire du site et à l'équipe PAG." : "Il n'est pas encore publié, ou cette adresse n'est rattachée à aucun site."}</p>
        <a href={BACKEND_URL} className="mt-8 inline-flex items-center h-11 px-6 bg-white text-[#0A0A0A] text-sm font-bold">Découvrir PAG</a>
      </div>
    </div>
  );
}
